"use client";

import * as React from "react";
import { LogIn } from "lucide-react";
import { cn } from "@baluu/ui/lib/utils";
import { Button } from "@baluu/ui/components/button";

export type CheckoutLoginGateProps = {
  /**
   * School name, woven into the copy. Falls back to a generic phrasing when
   * absent so the gate never renders a dangling "[School name]".
   */
  schoolName?: string;
  /** Invoked when the customer chooses to sign in / create an account. */
  onLogin: () => void;
  className?: string;
};

/**
 * Login wall shown before checkout when a school has `requireLoginToBook`
 * turned on. Rendered *in place of* the flow content, inside the checkout
 * theme provider, so it inherits the school's brand colour through the
 * `--primary` token (matching the rest of the checkout UI). It blocks the
 * flow but does not trap the visitor — the overlay's close button still works.
 */
export function CheckoutLoginGate({
  schoolName,
  onLogin,
  className,
}: CheckoutLoginGateProps) {
  const who = schoolName?.trim() ? schoolName.trim() : "This business";

  return (
    <div
      className={cn(
        "flex min-h-[50vh] w-full items-center justify-center px-4 py-10",
        className
      )}
    >
      <div className="w-full max-w-md rounded-2xl border bg-background p-8 text-center shadow-sm">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <LogIn className="h-7 w-7 text-primary" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-semibold text-foreground">
          Sign in to book
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {who} asks customers to sign in before booking. New here? You&apos;ll
          create your account in seconds.
        </p>
        <Button
          type="button"
          size="lg"
          onClick={onLogin}
          className="mt-6 w-full min-h-[44px] touch-manipulation"
        >
          Sign in / Create account
        </Button>
      </div>
    </div>
  );
}
